import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import { beehiveTheme } from '../../styles/beehiveTheme';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(15, 0),
  backgroundColor: 'white',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.75rem',
  marginBottom: theme.spacing(4),
  color: beehiveTheme.primary.main,
  lineHeight: 1.2,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#4a5568',
  lineHeight: 1.9,
  marginBottom: theme.spacing(4),
}));

const EmailBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: beehiveTheme.primary.main,
  borderRadius: '12px',
  marginBottom: theme.spacing(5),
  textAlign: 'center',
}));

const ContactBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  backgroundColor: '#fafafa',
  borderRadius: '12px',
  marginTop: theme.spacing(6),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: '8px',
    fontSize: '1rem',
    '& fieldset': {
      borderColor: '#d1d5db',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: beehiveTheme.accent.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: beehiveTheme.accent.main,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1rem',
    color: '#6b7280',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: beehiveTheme.accent.main,
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px 14px',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: beehiveTheme.accent.main,
  color: 'white',
  fontWeight: 600,
  fontSize: '1.1rem',
  padding: theme.spacing(1.75, 5),
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: beehiveTheme.accent.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(245, 158, 11, 0.3)',
  },
  transition: 'all 0.3s ease',
}));

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const mailtoLink = `mailto:support@wrootapp.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    
    window.location.href = mailtoLink;
    setSubmitted(true);
    
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <SectionContainer id="contact">
      <Container maxWidth="md">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Title>
            Let's talk.
          </Title>

          <Paragraph>
            We're currently building this system and looking for partners, collaborators, and early adopters. 
            If you're interested in beehive health monitoring, have ideas to share, or want to learn more about 
            our progress, we'd love to hear from you.
          </Paragraph>

          <EmailBox>
            <Typography sx={{ fontSize: '1rem', mb: 1.5, color: '#cbd5e0' }}>
              Reach us directly at:
            </Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'white' }}>
              support@wrootapp.com
            </Typography>
          </EmailBox>

          <Paragraph sx={{ fontSize: '1.05rem', mb: 2 }}>
            Or use the form below:
          </Paragraph>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <ContactBox>
            {submitted && (
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2.5, 
                  backgroundColor: '#d4edda', 
                  color: '#155724',
                  borderRadius: '8px',
                  border: '2px solid #c3e6cb',
                }}
              >
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  ✓ Thanks! Your email client should open shortly.
                </Typography>
              </Box>
            )}
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={5}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <SubmitButton
                    type="submit"
                    variant="contained"
                    endIcon={<SendIcon />}
                  >
                    Send Message
                  </SubmitButton>
                </Grid>
              </Grid>
            </form>
          </ContactBox>

          <Typography sx={{ fontSize: '0.95rem', color: '#718096', mt: 4, lineHeight: 1.8 }}>
            <strong>What we're looking for:</strong> Research partners, beekeepers interested in testing early 
            prototypes, investors who understand agriculture technology, and anyone with experience in colony 
            health monitoring who wants to contribute ideas.
          </Typography>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}
